import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import A from '@adsk/alloy-react-a';
import { AlertInformationIcon } from '@adsk/alloy-react-icon';
import Tooltip from '@adsk/alloy-react-tooltip';
import Divider from '@adsk/alloy-react-divider';
import { useLocale } from '@adsk/alloy-react-locale';
import { DATE_FORMAT } from '@adsk/acs-admin-ui-components-helpers';
import { projectDataShape } from '../../propTypes';
import {
  detailsViewWrapperStyle,
  detailsViewItemStyle,
  dividerStyle,
} from './styles';

const getDate = (date, locale, format) => {
  const dateTime = moment(date).locale(locale);
  return <time dateTime={dateTime.format()}>{dateTime.format(format)}</time>;
};

const DetailsView = ({ showAdditionalFields, data }) => {
  const { T, locale: localeName } = useLocale(({ locale }) =>
    require(`../../../i18n/${locale}.json`)
  );

  return (
    <div css={detailsViewWrapperStyle}>
      <div css={detailsViewItemStyle}>
        <h2>{T.translate('PROJECT_NAME')}</h2>
        <p>{data.name ? data.name : T.translate('PROJECT_NOT_AVAILABLE')}</p>
      </div>
      <div css={detailsViewItemStyle}>
        <h2>{T.translate('PROJECT_TYPE')}</h2>
        <p>{data.type ? data.type : T.translate('PROJECT_NOT_AVAILABLE')}</p>
      </div>
      <div css={detailsViewItemStyle}>
        <h2>{T.translate('PROJECT_ADDRESS')}</h2>
        <p>
          {(data.addressLine1 || data.addressLine2) && (
            <span>
              {data.addressLine1}
              {data.addressLine1 && data.addressLine2 && ', '}
              {data.addressLine2}
              <br />
            </span>
          )}
          {(data.city || data.stateOrProvince || data.postalCode) && (
            <span>
              {data.city}
              {data.city && (data.stateOrProvince || data.postalCode) && ', '}
              {data.stateOrProvince}
              {data.stateOrProvince && data.postalCode && ' '}
              {data.postalCode}
              <br />
            </span>
          )}
          {data.country && (
            <span>
              {data.country}
              <br />
            </span>
          )}
          {!data.country &&
            !data.postalCode &&
            !data.city &&
            !data.stateOrProvince &&
            !data.addressLine2 &&
            !data.addressLine1 &&
            T.translate('PROJECT_NOT_AVAILABLE')}
        </p>
      </div>
      {showAdditionalFields && (
        <>
          <div css={detailsViewItemStyle}>
            <h2>{T.translate('PROJECT_JOB_NUMBER')}</h2>
            <p>
              {data.jobNumber
                ? data.jobNumber
                : T.translate('PROJECT_NOT_AVAILABLE')}
            </p>
          </div>
          <div css={detailsViewItemStyle}>
            <h2>{T.translate('PROJECT_VALUE')}</h2>
            <p>
              {data.projectValue &&
              data.projectValue.currency &&
              typeof data.projectValue.value === 'number'
                ? new Intl.NumberFormat(localeName, {
                    style: 'currency',
                    currency: data.projectValue.currency,
                  }).format(data.projectValue.value)
                : T.translate('PROJECT_NOT_AVAILABLE')}
            </p>
          </div>
          <div css={detailsViewItemStyle}>
            <h2>{T.translate('PROJECT_TIMEZONE')}</h2>
            <Tooltip
              placement={Tooltip.PLACEMENTS.RIGHT}
              delayHide={2000}
              content={
                <>
                  <span>{T.translate('PROJECT_TIMEZONE_TOOLTIP_CONTENT')}</span>
                  <Divider style={dividerStyle} />
                  <span>
                    <A
                      target="_blank"
                      href="https://help.autodesk.com/view/DOCS/ENU/?guid=Project_Settings"
                    >
                      {T.translate('PROJECT_TIMEZONE_TOOLTIP_LEARN_MORE')}
                    </A>
                  </span>
                </>
              }
            >
              <AlertInformationIcon />
            </Tooltip>
            <p>
              {data.timezone
                ? data.timezone
                : T.translate('PROJECT_NOT_AVAILABLE')}
            </p>
          </div>
        </>
      )}
      <div css={detailsViewItemStyle}>
        <h2>{T.translate('PROJECT_START_DATE')}</h2>
        <Tooltip
          placement={Tooltip.PLACEMENTS.TOP_START}
          content={T.translate('PROJECT_DATES_TOOLTIP_CONTENT')}
        >
          <AlertInformationIcon />
        </Tooltip>
        <p>
          {data.startDate
            ? getDate(data.startDate, localeName, DATE_FORMAT)
            : T.translate('PROJECT_NOT_AVAILABLE')}
        </p>
      </div>
      <div css={detailsViewItemStyle}>
        <h2>{T.translate('PROJECT_END_DATE')}</h2>
        <Tooltip
          placement={Tooltip.PLACEMENTS.TOP_START}
          content={T.translate('PROJECT_DATES_TOOLTIP_CONTENT')}
        >
          <AlertInformationIcon />
        </Tooltip>
        <p>
          {data.endDate
            ? getDate(data.endDate, localeName, DATE_FORMAT)
            : T.translate('PROJECT_NOT_AVAILABLE')}
        </p>
      </div>
    </div>
  );
};

DetailsView.propTypes = {
  data: projectDataShape.isRequired,
  showAdditionalFields: PropTypes.bool.isRequired,
};

export default DetailsView;
